import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, ScrollView, Pressable, Image, Animated } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../navigation/RootNavigator';
import {
  PageContainer,
  ContentContainer,
  HeaderContainer,
  BottomContainer,
  Text,
  Button,
  Input,
  Card,
  Theme,
} from '../../components/ui';
import { PrivateProfileService } from '../../services/privateProfileService';
import { auth } from '../../config/firebase';
import { ExternalTransferService } from '../../services/externalTransferService';
import { ProfileExtractionService } from '../../services/profileExtractionService';
import { PrivateProfile } from '../../models/PrivateProfile';
import { markOnboardingComplete } from '../../utils/onboardingStorage';

type Props = NativeStackScreenProps<RootStackParamList, 'AbbreviatedOnboarding'>;

interface OnboardingStep {
  id: string;
  type: 'welcome_summary' | 'text_input' | 'single_select' | 'final_summary';
  title: string;
  subtitle?: string;
  options?: Array<{ id: string; label: string; description?: string }>;
  placeholder?: string;
  defaultValue?: string;
}

/**
 * AbbreviatedOnboardingScreen
 * 
 * Shortened onboarding for users transferring from external service.
 * STARTS with a welcome summary showing imported data (more personalized!)
 * Then asks a few key questions to personalize Mentorist experience.
 */
const AbbreviatedOnboardingScreen: React.FC<Props> = ({ route, navigation }) => {
  const { profile } = route.params as { profile: PrivateProfile };
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({
    name: profile.displayName || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExtractingData, setIsExtractingData] = useState(false);
  const [extractionComplete, setExtractionComplete] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const userId = auth.currentUser?.uid;
  
  // Animated progress bar (0-100%)
  const progressAnim = useRef(new Animated.Value(0)).current;

  // Animate progress bar while extracting (simulate ~40 seconds to match actual API time)
  useEffect(() => {
    if (isExtractingData && !extractionComplete) {
      // Animate from current value to 90% over 40 seconds (matches typical Phase 1 + Phase 2 duration)
      Animated.timing(progressAnim, {
        toValue: 90,
        duration: 40000,
        useNativeDriver: false,
      }).start();
    } else if (extractionComplete) {
      // Complete to 100% quickly
      Animated.timing(progressAnim, {
        toValue: 100,
        duration: 300,
        useNativeDriver: false,
      }).start();
    } else {
      // Reset
      progressAnim.setValue(0);
    }
  }, [isExtractingData, extractionComplete]);

  // Trigger AI extraction + recommendations if needed (at top level, not in render function!)
  React.useEffect(() => {
    const needsExtraction = ExternalTransferService.needsAIExtraction(profile);
    const isWelcomeStep = currentStepIndex === 0;

    if (!needsExtraction && !extractionComplete) {
      setExtractionComplete(true);
      return;
    }

    if (!needsExtraction || !isWelcomeStep || isExtractingData || extractionComplete) {
      return;
    }

    if (!userId) {
      console.warn('🛑 AbbreviatedOnboarding: missing authenticated user, waiting for auth...');
      return;
    }

    setExtractionError(null);
    setIsExtractingData(true);

    const startTime = Date.now();

    ProfileExtractionService.extractAndGenerateRecommendations(userId)
      .then(result => {
        const duration = Date.now() - startTime;

        if (result.success) {
          setExtractionComplete(true);

          // Log success to analytics
          const { loggingService } = require('../../services/loggingService');
          const { userContext } = require('../../utils/logging/actions');
          loggingService.log(userContext({
            aiExtractionSuccess: true,
            extractionDuration: duration,
            hasCalculationResults: !!profile.calculationResults,
            answersCount: profile.onboardingAnswers?.answers?.length || 0,
          }));
        } else {
          const errorMessage = result.error || 'AI extraction failed';
          setExtractionError(errorMessage);

          const { loggingService } = require('../../services/loggingService');
          const { errorOccurred } = require('../../utils/logging/actions');
          loggingService.log(errorOccurred(
            errorMessage,
            'ai_extraction_failed',
            {
              duration,
              hasOnboardingData: (profile.onboardingAnswers?.answers?.length || 0) > 0,
              hasCalculationResults: !!profile.calculationResults,
            }
          ));

          if (__DEV__) {
            throw new Error(errorMessage);
          }

          // Allow flow to continue but highlight error
          setExtractionComplete(true);
        }
      })
      .catch(error => {
        const duration = Date.now() - startTime;
        const errorMessage = error?.message || 'AI extraction exception';

        const { loggingService } = require('../../services/loggingService');
        const { errorOccurred } = require('../../utils/logging/actions');
        loggingService.log(errorOccurred(
          errorMessage,
          'ai_extraction_exception',
          {
            duration,
            errorName: error?.name || 'Error',
          }
        ));

        setExtractionError(errorMessage);

        if (__DEV__) {
          throw error;
        }

        // Allow flow to continue but highlight error
        setExtractionComplete(true);
      })
      .finally(() => setIsExtractingData(false));
  }, [currentStepIndex, profile, isExtractingData, extractionComplete, userId]);

  const steps: OnboardingStep[] = [
    {
      id: 'welcome_summary',
      type: 'welcome_summary',
      title: `Welcome${profile.displayName ? `, ${profile.displayName}` : ''}!`,
      subtitle: "Here's what we know about you:",
    },
    {
      id: 'name',
      type: 'text_input',
      title: "What should we call you?",
      subtitle: "Your preferred name for a personal touch",
      placeholder: "Your name",
      defaultValue: '', // Don't pre-fill with email, let user type their real name
    },
    {
      id: 'final_summary',
      type: 'final_summary',
      title: "You're all set!",
      subtitle: "Your mentor has reviewed your profile and is ready to help you grow.",
    },
  ];

  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const canProceed = validateCurrentStep();

  function validateCurrentStep(): boolean {
    if (currentStep.type === 'welcome_summary' || currentStep.type === 'final_summary') {
      return true; // These steps don't require input
    }
    if (currentStep.type === 'text_input') {
      return (answers[currentStep.id]?.trim() || '').length > 0;
    }
    if (currentStep.type === 'single_select') {
      return !!answers[currentStep.id];
    }
    return false;
  }

  const handleNext = async () => {
    if (isLastStep) {
      await handleComplete();
    } else {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    if (!auth.currentUser) return;

    // Wait for extraction/recommendations to complete before proceeding
    if (!extractionComplete) {
      console.log('⏳ Waiting for recommendations to complete...');
      setIsSubmitting(true);
      return; // Button will be disabled, user waits
    }

    setIsSubmitting(true);

    try {
      // Update profile with user's name only
      // (activeContext already has data from AI extraction)
      await PrivateProfileService.updateProfile(auth.currentUser.uid, {
        displayName: answers.name || profile.displayName,
      });

      console.log('✅ Profile completed for user', auth.currentUser.uid);

      // Mark onboarding as complete so user doesn't see it again after refresh
      await markOnboardingComplete();
      console.log('✅ Onboarding marked as complete');

      // Navigate to main app with Home tab
      // Recommendations are ready, so user will see them immediately
      navigation.reset({
        index: 0,
        routes: [
          {
            name: 'Main',
            params: {
              screen: 'Home',
            },
          },
        ],
      });
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // TODO: Show error message
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep.type) {
      case 'welcome_summary':
        return renderWelcomeSummaryStep();
      case 'text_input':
        return renderTextInputStep();
      case 'final_summary':
        return renderFinalSummaryStep();
      default:
        return null;
    }
  };

  const renderWelcomeSummaryStep = () => {
    const summaryText = ExternalTransferService.generateWelcomeSummary(profile);
    const needsExtraction = ExternalTransferService.needsAIExtraction(profile);
    
    return (
      <View style={styles.stepContent}>
        <Text variant="body1" color="secondary" align="center" style={styles.stepSubtitle}>
          {currentStep.subtitle}
        </Text>
        <Card style={styles.welcomeSummaryCard}>
          <Text variant="body1" style={styles.summaryText}>
            {summaryText}
          </Text>
          {needsExtraction && isExtractingData && (
            <Text variant="caption" color="secondary" style={styles.extractingHint}>
              Our AI is analyzing your responses and preparing personalized book recommendations...
            </Text>
          )}
        </Card>
        <Text variant="body2" color="secondary" align="center" style={styles.continueHint}>
          Let's personalize your Mentorist experience with just a couple quick questions.
        </Text>
      </View>
    );
  };

  const renderTextInputStep = () => (
    <View style={styles.stepContent}>
      <Text variant="body1" color="secondary" align="center" style={styles.stepSubtitle}>
        {currentStep.subtitle}
      </Text>
      <Input
        value={answers[currentStep.id] || currentStep.defaultValue || ''}
        onChangeText={(text) => setAnswers(prev => ({ ...prev, [currentStep.id]: text }))}
        placeholder={currentStep.placeholder}
        autoFocus
        style={styles.input}
      />
    </View>
  );


  const renderFinalSummaryStep = () => {
    const userName = answers.name || profile.displayName || 'there';
    const needsExtraction = ExternalTransferService.needsAIExtraction(profile);
    const stillProcessing = needsExtraction && !extractionComplete;
    
    // Calculate progress percentage for display
    const progressWidth = progressAnim.interpolate({
      inputRange: [0, 100],
      outputRange: ['0%', '100%'],
    });
    
    return (
      <View style={styles.stepContent}>
        <Text variant="body1" color="secondary" align="center" style={styles.stepSubtitle}>
          {currentStep.subtitle}
        </Text>
        
        {/* Mentor Section - Matching Home Page Style */}
        <View style={styles.mentorSection}>
          <View style={styles.mentorContent}>
            <View style={styles.mentorAvatar}>
              <Image 
                source={require('../../assets/mentor/mentor-default.webp')}
                style={styles.mascotImage}
              />
            </View>
            <View style={styles.mentorTextContainer}>
              <Text style={styles.mentorLabel}>Your Mentor</Text>
              <Text style={styles.mentorMessage}>
                Hi {userName}! 👋 I've reviewed your profile and I'm excited to help you on your journey.
                Your personalized mentor is ready with book recommendations, insights, and guidance.
              </Text>
            </View>
          </View>
          
          {/* Animated Progress Bar */}
          {stillProcessing && (
            <View style={styles.progressContainer}>
              <View style={styles.progressBarBackground}>
                <Animated.View 
                  style={[
                    styles.progressBarFill,
                    { width: progressWidth }
                  ]} 
                />
              </View>
              <Text variant="caption" style={styles.progressText}>
                Preparing your personalized recommendations...
              </Text>
            </View>
          )}
        </View>

        {extractionError && (
          <Text variant="caption" color="error" align="center" style={styles.mentorNote}>
            AI extraction failed: {extractionError}
          </Text>
        )}
        
        <Text variant="body2" color="secondary" align="center" style={styles.mentorNote}>
          {stillProcessing 
            ? 'Almost ready! Your books and insights are being prepared...'
            : 'Let\'s explore your personalized book recommendations!'}
        </Text>
      </View>
    );
  };

  return (
    <PageContainer>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <ContentContainer>
          <HeaderContainer>
            <View style={styles.progressBar}>
              {steps.map((_, idx) => (
                <View
                  key={idx}
                  style={[
                    styles.progressDot,
                    idx <= currentStepIndex && styles.progressDotActive,
                  ]}
                />
              ))}
            </View>
            <Text variant="heading2" align="center" style={styles.title}>
              {currentStep.title}
            </Text>
          </HeaderContainer>

          {renderStep()}
        </ContentContainer>
      </ScrollView>

      <BottomContainer>
        <View style={styles.buttonRow}>
          {currentStepIndex > 0 && (
            <Button
              title="Back"
              variant="secondary"
              onPress={handleBack}
              style={styles.backButton}
              disabled={isSubmitting}
            />
          )}
          <Button
            title={
              isLastStep 
                ? (isSubmitting || (ExternalTransferService.needsAIExtraction(profile) && !extractionComplete)
                    ? 'Preparing...' 
                    : 'Start Your Journey')
                : 'Continue'
            }
            onPress={handleNext}
            disabled={
              !canProceed || 
              isSubmitting || 
              (isLastStep && ExternalTransferService.needsAIExtraction(profile) && !extractionComplete)
            }
            style={styles.nextButton}
          />
        </View>
      </BottomContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
  },
  progressBar: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: Theme.spacing.sm,
    marginBottom: Theme.spacing.xl,
  },
  progressDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Theme.colors.border,
  },
  progressDotActive: {
    backgroundColor: Theme.colors.primary,
  },
  title: {
    marginBottom: Theme.spacing.md,
  },
  stepContent: {
    gap: Theme.spacing.xl,
  },
  stepSubtitle: {
    marginTop: Theme.spacing.md,
  },
  input: {
    marginTop: Theme.spacing.lg,
  },
  welcomeSummaryCard: {
    marginTop: Theme.spacing.xl,
    padding: Theme.spacing.xl,
    backgroundColor: Theme.colors.primaryLight,
  },
  summaryText: {
    lineHeight: 24,
  },
  continueHint: {
    marginTop: Theme.spacing.xl,
  },
  extractingHint: {
    marginTop: Theme.spacing.md,
    fontStyle: 'italic',
  },
  optionsContainer: {
    gap: Theme.spacing.md,
    marginTop: Theme.spacing.lg,
  },
  optionCard: {
    padding: Theme.spacing.lg,
    borderWidth: 2,
    borderColor: Theme.colors.border,
    borderRadius: Theme.borderRadius.md,
    backgroundColor: Theme.colors.surface,
  },
  optionCardSelected: {
    borderColor: Theme.colors.primary,
    backgroundColor: Theme.colors.primaryLight,
  },
  optionLabel: {
    fontFamily: Theme.typography.fontFamily.medium,
  },
  optionLabelSelected: {
    color: Theme.colors.primary,
  },
  optionDescription: {
    marginTop: Theme.spacing.xs,
  },
  summaryCard: {
    padding: Theme.spacing.lg,
    marginTop: Theme.spacing.lg,
  },
  summaryLabel: {
    marginBottom: Theme.spacing.xs,
  },
  summaryValue: {
    color: Theme.colors.primary,
  },
  // Mentor Section Styles (matching HomePage.tsx)
  mentorSection: {
    backgroundColor: 'rgba(240, 253, 250, 1)', // teal-50
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.xl,
    borderRadius: Theme.borderRadius.md,
    borderWidth: 1,
    borderColor: 'rgba(153, 246, 228, 0.5)', // teal-200
    marginTop: Theme.spacing.lg,
  },
  mentorContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Theme.spacing.md,
  },
  mentorAvatar: {
    width: 40,
    height: 48,
    borderRadius: Theme.borderRadius.md,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  mascotImage: {
    width: '100%',
    height: '100%',
    transform: [{ scaleX: -1 }], // Mirror image to match home page
  },
  mentorTextContainer: {
    flex: 1,
    gap: 4,
  },
  mentorLabel: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.medium,
    color: 'rgba(15, 118, 110, 0.8)', // teal-700
  },
  mentorMessage: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: 'rgba(31, 41, 55, 1)', // gray-800
    lineHeight: 20,
  },
  // Progress Bar Styles
  progressContainer: {
    marginTop: Theme.spacing.lg,
    gap: Theme.spacing.sm,
  },
  progressBarBackground: {
    height: 6,
    backgroundColor: 'rgba(153, 246, 228, 0.3)', // teal-200 with opacity
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: Theme.colors.primary,
    borderRadius: 3,
  },
  progressText: {
    color: 'rgba(15, 118, 110, 0.8)', // teal-700
    fontStyle: 'italic',
    textAlign: 'center',
  },
  mentorNote: {
    marginTop: Theme.spacing.lg,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: Theme.spacing.md,
  },
  backButton: {
    flex: 1,
  },
  nextButton: {
    flex: 2,
  },
});

export default AbbreviatedOnboardingScreen;

